"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import Link from "next/link";
import { MoveRight } from "lucide-react";
import { ARCHIVES, ABOUT_GIRMITIYA, mediaPath } from "@/lib/media";
const ARCHIVES_STYLE = `
    .archive-grain::after {
        content: '';
        position: absolute;
        inset: 0;
        background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
        pointer-events: none;
        z-index: 0;
    }
`;
const archives = [{
  image: ABOUT_GIRMITIYA.meaningAndHistory.src,
  alt: ABOUT_GIRMITIYA.meaningAndHistory.alt,
  number: "01",
  aspect: "aspect-[3/4]",
  title: "Origins",
  desc: "Meaning and History of 'Girmitiya'"
}, {
  image: ABOUT_GIRMITIYA.indentureSystem.src,
  alt: ABOUT_GIRMITIYA.indentureSystem.alt,
  number: "02",
  aspect: "aspect-[1/1]",
  title: "System",
  desc: "The Indenture System"
}, {
  image: ABOUT_GIRMITIYA.causesOfMigration.src,
  alt: ABOUT_GIRMITIYA.causesOfMigration.alt,
  number: "03",
  aspect: "aspect-[3/4]",
  title: "Migration",
  desc: "Causes of Migration from India"
}, {
  image: ABOUT_GIRMITIYA.culturalImpact.src,
  alt: ABOUT_GIRMITIYA.culturalImpact.alt,
  number: "04",
  aspect: "aspect-[4/3]",
  title: "Legacy",
  desc: "Social, Economic, and Cultural Impact"
}];

// Interactive 3D Tilt Card Component
const TiltCard = t0 => {
  const $ = _c(42);
  const {
    item
  } = t0;
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      stiffness: 150,
      damping: 25
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const mouseXSpring = useSpring(x, t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      stiffness: 150,
      damping: 25
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const mouseYSpring = useSpring(y, t2);
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [-0.5, 0.5];
    t4 = ["10deg", "-10deg"];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  const rotateX = useTransform(mouseYSpring, t3, t4);
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [-0.5, 0.5];
    t6 = ["-10deg", "10deg"];
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  const rotateY = useTransform(mouseXSpring, t5, t6);
  let t7;
  if ($[6] !== x || $[7] !== y) {
    t7 = e => {
      const rect = e.currentTarget.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const xPct = mouseX / width - 0.5;
      const yPct = mouseY / height - 0.5;
      x.set(xPct);
      y.set(yPct);
    };
    $[6] = x;
    $[7] = y;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleMouseMove = t7;
  let t8;
  if ($[9] !== x || $[10] !== y) {
    t8 = () => {
      x.set(0);
      y.set(0);
    };
    $[9] = x;
    $[10] = y;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const handleMouseLeave = t8;
  let t9;
  if ($[12] !== rotateX || $[13] !== rotateY) {
    t9 = {
      rotateX,
      rotateY,
      transformStyle: "preserve-3d",
      perspective: 1000
    };
    $[12] = rotateX;
    $[13] = rotateY;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      translateZ: 50
    };
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const t11 = `relative overflow-hidden ${item.aspect} w-full shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-white/5 bg-[#0F1C2E]`;
  let t12;
  if ($[16] !== item.image) {
    t12 = mediaPath(item.image);
    $[16] = item.image;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== item.alt || $[19] !== t12) {
    t13 = <img src={t12} alt={item.alt} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale-[40%] sepia-[20%] contrast-[1.1] transition-all duration-700 group-hover:scale-110 group-hover:grayscale-0 group-hover:sepia-0" />;
    $[18] = item.alt;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="absolute inset-0 bg-gradient-to-t from-[#0F1C2E] via-[#0F1C2E]/20 to-transparent opacity-80 group-hover:opacity-60 transition-opacity duration-500" />;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== item.number) {
    t15 = <div className="absolute top-4 right-4 sm:top-6 sm:right-6"><span className="font-display text-[24px] italic text-[#C9A84C] opacity-60">{item.number}</span></div>;
    $[22] = item.number;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="w-8 h-px bg-[#DF6D2A] mb-4 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 delay-100" />;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== item.title) {
    t17 = <h3 className="font-display text-[22px] sm:text-[28px] md:text-[32px] text-[#F4EFE6] leading-snug mb-2">{item.title}</h3>;
    $[25] = item.title;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== item.desc) {
    t18 = <p className="font-body text-[13px] sm:text-[14px] text-white/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-200">{item.desc}</p>;
    $[27] = item.desc;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t17 || $[30] !== t18) {
    t19 = <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500 ease-out">{t16}{t17}{t18}</div>;
    $[29] = t17;
    $[30] = t18;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== t11 || $[33] !== t13 || $[34] !== t15 || $[35] !== t19) {
    t20 = <motion.div style={t10} className={t11}>{t13}{t14}{t15}{t19}</motion.div>;
    $[32] = t11;
    $[33] = t13;
    $[34] = t15;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== handleMouseLeave || $[38] !== handleMouseMove || $[39] !== t20 || $[40] !== t9) {
    t21 = <motion.div onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} style={t9} className="group cursor-pointer relative">{t20}</motion.div>;
    $[37] = handleMouseLeave;
    $[38] = handleMouseMove;
    $[39] = t20;
    $[40] = t9;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  return t21;
};
const Archives = () => {
  const $ = _c(17);
  const containerRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      target: containerRef,
      offset: ["start end", "end start"]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    scrollYProgress
  } = useScroll(t0);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [0, 1];
    t2 = [100, -100];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const yOffset = useTransform(scrollYProgress, t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <style>{ARCHIVES_STYLE}</style>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex items-center gap-2 sm:gap-3 mb-5 sm:mb-8"><div className="w-8 h-px bg-[#DF6D2A]" /><span className="font-body text-[10.5px] tracking-[0.25em] uppercase text-accent-readable font-light">Digital Collection</span></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <h2 className="font-display text-[36px] sm:text-[48px] md:text-[64px] font-light leading-[1.05] tracking-[-0.02em] text-[#F4EFE6] mb-4 sm:mb-6">Archive <em className="text-[#C9A84C] italic">Spotlight</em></h2>;
    t6 = <p className="font-body text-[14px] sm:text-[15px] font-light leading-[1.7] sm:leading-[1.85] text-white/60 max-w-[400px] mb-8 sm:mb-12">Explore primary sources, photographs, and documents from across the Girmitiya diaspora. Our curated collection preserves the tactile history of the indenture period.</p>;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="w-full lg:w-[35%] lg:sticky lg:top-32 flex flex-col items-start pt-4 sm:pt-10">{t4}{t5}{t6}<Link href="/archives" className="font-body inline-flex items-center justify-center gap-2.5 px-6 sm:px-8 py-3.5 sm:py-4 border border-white/20 text-[#F4EFE6] text-[11.5px] sm:text-[12.5px] tracking-[0.08em] uppercase hover:bg-[#F4EFE6] hover:text-[#0F1C2E] transition-colors duration-300 group w-full sm:w-auto">View Full Archive<MoveRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" /></Link></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="sm:hidden w-full grid grid-cols-2 gap-3 relative">{archives.map(_temp)}</div>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <motion.div initial={{
      opacity: 0,
      y: 50
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.8
    }}><TiltCard item={archives[0]} /></motion.div>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="flex flex-col gap-6 sm:gap-8 md:gap-16 sm:mt-24">{t9}<motion.div initial={{
        opacity: 0,
        y: 50
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }}><TiltCard item={archives[3]} /></motion.div></div>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== yOffset) {
    t11 = {
      y: yOffset
    };
    $[11] = yOffset;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <motion.div initial={{
      opacity: 0,
      y: 50
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.8,
      delay: 0.1
    }}><TiltCard item={archives[1]} /></motion.div>;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <motion.div initial={{
      opacity: 0,
      y: 50
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.8,
      delay: 0.3
    }}><TiltCard item={archives[2]} /></motion.div>;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== t11) {
    t14 = <>{t3}<section ref={containerRef} className="relative font-body bg-[#0F1C2E] py-16 sm:py-24 md:py-32 overflow-hidden archive-grain"><div className="max-w-[1280px] mx-auto px-4 sm:px-6 md:px-8 relative z-10"><div className="flex flex-col lg:flex-row gap-10 sm:gap-16 items-start">{t7}{t8}<div className="hidden sm:grid w-full lg:w-[65%] grid-cols-2 gap-6 sm:gap-8 md:gap-12 relative">{t10}<motion.div style={t11} className="flex flex-col gap-8 md:gap-16 sm:-mt-12">{t12}{t13}</motion.div></div></div></div></section></>;
    $[15] = t11;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  return t14;
};
export default Archives;
function _temp(item) {
  return <div key={item.number} className="relative overflow-hidden aspect-[3/4] bg-[#0F1C2E] group cursor-pointer"><img src={mediaPath(item.image)} alt={item.alt} className="w-full h-full object-cover grayscale-[30%] sepia-[15%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" /><div className="absolute inset-0 bg-gradient-to-t from-[#0F1C2E] via-[#0F1C2E]/20 to-transparent opacity-80" /><div className="absolute top-3 right-3"><span className="font-display text-[18px] italic text-[#C9A84C] opacity-60">{item.number}</span></div></div>;
}